import React, { useState, useEffect, useCallback } from "react";
import Styles from "./TraderEntity.module.css";
import {
  createTraderMap,
  updateTraderMap,
  deleteTraderMap,
  getTraderMap,
} from "../../../../Services/Admin/Alter";
import {
  ErrorMessage,
  SuccessMessage,
} from "../../../../commons/Toast/ErrorMessage";
import ConfirmationModal from "../../../../commons/ConfirmationModal/ConfirmationModal";
import TextInput from "../../../../commons/Inputs/TextInput/TextInput";
import SelectInput from "../../../../commons/Inputs/Select/SelectInput";
import Fileupload from "../../../../commons/Inputs/FileUpload/Fileupload";
import { Modal } from "@mui/material";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

const entityTypeList = [
  { id: "CONSUMER", name: "Consumer" },
  { id: "GENERATOR", name: "Generator" },
];

const emptyForm = {
  entityType: "",
  entityName: "",
  docId: "",
};

const TraderEntity = ({ companyId, userId }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState(entityTypeList[0]);
  const [formModal, setFormModal] = useState({ open: false, editData: null });
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [deleting, setDeleting] = useState(false);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    const res = await getTraderMap(companyId, filterType?.id);
    if (res?.data?.code === 200) {
      setRecords(res?.data?.data || []);
    } else {
      ErrorMessage(res?.data?.error || "Failed to fetch records");
    }
    setLoading(false);
  }, [companyId, filterType]);

  useEffect(() => {
    if (companyId) fetchRecords();
  }, [companyId, filterType, fetchRecords]);

  const openAddModal = () => {
    setFormData(emptyForm);
    setErrors({});
    setFormModal({ open: true, editData: null });
  };

  const openEditModal = (record) => {
    setFormData({
      entityType: record.entityType,
      entityName: record.entityName,
      docId: record.docId,
    });
    setErrors({});
    setFormModal({ open: true, editData: record });
  };

  const closeFormModal = () => {
    setFormModal({ open: false, editData: null });
    setFormData(emptyForm);
    setErrors({});
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.entityType) newErrors.entityType = "Entity type is required";
    if (!formData.entityName?.trim())
      newErrors.entityName = "Entity name is required";
    if (!formData.docId) newErrors.docId = "Document is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleFileChange = ({ id }) => {
    setFormData((prev) => ({ ...prev, docId: id }));
    if (errors.docId) setErrors((prev) => ({ ...prev, docId: "" }));
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);

    const payload = {
      companyId: Number(companyId),
      entityType: formData.entityType,
      entityName: formData.entityName,
      docId: formData.docId,
      userId: Number(userId),
    };

    if (formModal.editData) {
      payload.id = formModal.editData.id;
    }

    const res = formModal.editData
      ? await updateTraderMap(payload)
      : await createTraderMap(payload);

    if (res?.data?.code === 200) {
      SuccessMessage(res?.data?.message || "Saved successfully");
      closeFormModal();
      fetchRecords();
    } else {
      ErrorMessage(res?.data?.error || "Failed to save");
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    const res = await deleteTraderMap(deleteModal.id);
    if (res?.data?.code === 200) {
      SuccessMessage(res?.data?.message || "Deleted successfully");
      setDeleteModal({ open: false, id: null });
      fetchRecords();
    } else {
      ErrorMessage(res?.data?.error || "Failed to delete");
    }
    setDeleting(false);
  };

  return (
    <div className={`display_flex_column_gap_8 ${Styles.container}`}>
      {/* Header */}
      <div className={Styles.header}>
        <div className={Styles.sectionTitle}>Trader Entity Mapping</div>
        <div className={Styles.headerRight}>
          <SelectInput
            list={entityTypeList}
            value={filterType}
            onChange={(val) => setFilterType(val)}
            example="Filter by Type"
            height={36}
          />
          <button className={Styles.addBtn} onClick={openAddModal}>
            <AddIcon style={{ fontSize: "16px" }} />
            Add
          </button>
        </div>
      </div>

      {/* Table */}
      <div className={Styles.tableWrapper}>
        {loading ? (
          <div className={Styles.loadingText}>Loading...</div>
        ) : records.length === 0 ? (
          <div className={Styles.emptyText}>No records found</div>
        ) : (
          <table className={Styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th>Entity Name</th>
                <th>Entity Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record, index) => (
                <tr key={record.id}>
                  <td>{index + 1}</td>
                  <td>{record.entityName}</td>
                  <td>
                    <span
                      className={`${Styles.badge} ${Styles[record.entityType?.toLowerCase()]}`}
                    >
                      {record.entityType}
                    </span>
                  </td>
                  <td>
                    <div className={Styles.actionBtns}>
                      <button
                        className={Styles.editBtn}
                        onClick={() => openEditModal(record)}
                      >
                        <BorderColorIcon style={{ fontSize: "14px" }} />
                      </button>
                      <button
                        className={Styles.deleteBtn}
                        onClick={() =>
                          setDeleteModal({ open: true, id: record.id })
                        }
                      >
                        <DeleteIcon style={{ fontSize: "14px" }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add / Edit Modal */}
      {formModal.open && (
        <Modal open={formModal.open} onClose={closeFormModal}>
          <div className={`popupModal ${Styles.formModal}`}>
            <div className={Styles.modalHeader}>
              <div className="headingText">
                {formModal.editData
                  ? "Edit Trader Entity"
                  : "Add Trader Entity"}
              </div>
            </div>
            <div className={Styles.formBody}>
              <SelectInput
                heading="Entity Type"
                list={entityTypeList}
                value={formData.entityType}
                onChange={(val) =>
                  handleFieldChange("entityType", val?.id || val)
                }
                example="Select entity type"
                height={40}
                error={errors.entityType}
              />
              <TextInput
                heading="Entity Name"
                value={formData.entityName}
                onChange={(val) => handleFieldChange("entityName", val)}
                example="Enter entity name"
                error={errors.entityName}
                height={40}
              />
              <Fileupload
                heading="Document *"
                value={formData.docId}
                onChange={handleFileChange}
                field="docId"
                id="traderEntityDoc"
                accept=".pdf"
                height={45}
                example="Upload document (PDF)"
                error={errors.docId}
              />
            </div>
            <div className={Styles.formFooter}>
              <button
                className={Styles.cancelBtn}
                onClick={closeFormModal}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className={Styles.submitBtn}
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting
                  ? "Saving..."
                  : formModal.editData
                    ? "Update"
                    : "Save"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation */}
      {deleteModal.open && (
        <ConfirmationModal
          open={deleteModal.open}
          handleClose={() => setDeleteModal({ open: false, id: null })}
          header="Delete Trader Entity"
          data="Are you sure you want to delete this trader entity mapping?"
          handleSubmit={handleDelete}
          loading={deleting}
        />
      )}
    </div>
  );
};

export default TraderEntity;
